import { Typography } from "@mui/material";
import { useState, useRef, useCallback } from "react";
import { ThreadItemContainer, ThreadActions } from "./styles";
import { ThreadActionsMenu } from "./ThreadActionsMenu";
import { Thread } from "@store/threads.store";
import { classes } from "@shared/utils/classes";

type ThreadItemProps = {
  thread: Thread;
  isFirstThread: boolean;
  handleClickThread: (threadId: string) => void;
  handleDeleteClick: (threadId: string) => Promise<void>;
  handlePinClick: (threadId: string) => void;
  handleUnpinClick: (threadId: string) => void;
  currentThread: string | undefined;
  onRenameThread: (editThreadId: string, title: string) => void;
};

export const ThreadItem = (props: ThreadItemProps) => {
  const {
    thread,
    currentThread,
    handleClickThread,
    onRenameThread,
    handleDeleteClick,
    handlePinClick,
    handleUnpinClick,
    isFirstThread,
  } = props;
  const [editThreadId, setEditThreadId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const handleRenameClick = (threadId: string) => {
    setEditThreadId(threadId);
    setTimeout(() => {
      editInputRef.current?.focus();
    }, 300);
  };

  const handleEsc = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setEditThreadId(null);
    }
  };

  const handleRenameThread = useCallback(() => {
    if (!editInputRef.current || !editThreadId) return;
    const title = editInputRef.current.value.trim();
    onRenameThread(editThreadId, title);
    setEditThreadId(null);
  }, [editThreadId, onRenameThread]);

  const handleSubmitEditThreadTitle = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleRenameThread();
    },
    [handleRenameThread]
  );

  return (
    <ThreadItemContainer
      key={thread.id}
      className={classes([
        isFirstThread && "sidekick-sidebar-first-chat-thread",
        currentThread === thread.id && "active",
      ])}
      onClick={() => handleClickThread(thread.id)}
      title={thread.title}
    >
      {editThreadId === thread.id ? (
        <form onSubmit={handleSubmitEditThreadTitle}>
          <input
            type="text"
            defaultValue={thread.title}
            autoFocus
            name={`thread-${thread.id}`}
            onBlur={handleRenameThread}
            onClick={(e) => e.stopPropagation()}
            ref={editInputRef}
            onKeyDown={handleEsc}
            maxLength={40}
          />
        </form>
      ) : (
        <Typography>
          <span>{thread.title}</span>
        </Typography>
      )}
      {!editThreadId && (
        <ThreadActions onClick={(e) => e.stopPropagation()}>
          <ThreadActionsMenu
            onRenameClick={() => handleRenameClick(thread.id)}
            onDeleteClick={() => handleDeleteClick(thread.id)}
            onPinClick={() => handlePinClick(thread.id)}
            onUnpinClick={() => handleUnpinClick(thread.id)}
            pinned={thread.pinned}
          />
        </ThreadActions>
      )}
    </ThreadItemContainer>
  );
};
